import { useMemo, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import parse, { DOMNode, Element } from 'html-react-parser';
import { marked } from 'marked';
import { useParams } from 'react-router-dom';

import { Reference } from './reference';
import { ContentBlock, KimMessageType, ReferenceType } from '../../../../../../../../../../../backend/src/types/types';
import { Icon } from '@/new/components/icon';
import { Button } from '@/new/components/shadcn/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/new/components/shadcn/tooltip';
import { cn } from '@/new/lib/utils';

const SHOW_DEBUG_INFO = false;

const REF_PLACEHOLDER_TAG = 'kimref';

export function KimMessage({
  type,
  content,
  isComplete,
  referenceIndexMap,
  debugLabel,
  onReferenceClick,
}: {
  type: KimMessageType;
  content: ContentBlock[];
  isComplete: boolean;
  referenceIndexMap: Map<string, number>;
  debugLabel: string;
  onReferenceClick?: (id: string, referenceType: ReferenceType) => void;
}) {
  const { matterId, subsetId } = useParams();
  const isUser = type === KimMessageType.User;
  const [copied, setCopied] = useState(false);

  const firstReference = useMemo(() => {
    const ref = content.find((block) => block.type === 'reference' && block.refType === ReferenceType.Fact);
    if (ref && ref.type === 'reference') return { id: ref.id, refType: ref.refType };
    return null;
  }, [content]);

  const rendered = useMemo(() => {
    if (isUser) {
      const raw = content
        .filter((block) => block.type === 'text')
        .map((block) => block.value)
        .join('');

      // Render doc mentions as links to the doc page
      const parts: (string | JSX.Element)[] = [];
      const docMentionRegex = /@\[([^\]]+)\]\(([^)]+)\)/g;
      let lastIndex = 0;
      let match: RegExpExecArray | null;

      while ((match = docMentionRegex.exec(raw)) !== null) {
        if (match.index > lastIndex) {
          parts.push(raw.slice(lastIndex, match.index));
        }
        const [, fileName, docId] = match;
        parts.push(
          <a
            key={match.index}
            href={`/matters/${matterId}/subsets/${subsetId}/docs/${docId}`}
            target="_blank"
            className="group"
          >
            {fileName}
            <Icon className="lucide--external-link mr-0.5 -mb-px ml-1.25 inline-block text-xs" />
          </a>,
        );
        lastIndex = match.index + match[0].length;
      }

      if (lastIndex < raw.length) {
        parts.push(raw.slice(lastIndex));
      }

      return parts.some((p) => typeof p !== 'string') ? parts : raw;
    }

    // Build a single markdown string with placeholders for references
    const combined = content
      .map((block) => {
        if (block.type === 'text') return block.value;
        return `<${REF_PLACEHOLDER_TAG} data-id="${block.id}" data-ref-type="${block.refType}"></${REF_PLACEHOLDER_TAG}>`;
      })
      .join('');

    const html = marked.parse(combined, { async: false }) as string;

    return parse(html, {
      replace: (domNode: DOMNode) => {
        if (domNode instanceof Element && domNode.name === REF_PLACEHOLDER_TAG) {
          const id = domNode.attribs['data-id'];
          const refType = domNode.attribs['data-ref-type'] as ReferenceType;
          return (
            <Reference
              index={referenceIndexMap.get(id) ?? 0}
              id={id}
              referenceType={refType}
              onClick={refType === ReferenceType.Fact ? onReferenceClick : undefined}
            />
          );
        }
      },
    });
  }, [content, isUser, referenceIndexMap, onReferenceClick]);

  const copyToClipboard = () => {
    const markdown = content
      .filter((block) => block.type === 'text')
      .map((block) => block.value)
      .join('');
    navigator.clipboard.writeText(markdown);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className={cn('flex', isUser ? 'justify-end' : '')}>
      <div
        className={cn(
          'rounded-xl px-3.5 py-2',
          isUser ? 'my-8 max-w-4/5 rounded-br-none border border-gray-300 bg-gray-100' : 'w-full',
        )}
      >
        {SHOW_DEBUG_INFO && (
          <div className="mb-1 font-mono text-xs text-gray-400">
            {debugLabel} | type: {type}
          </div>
        )}

        <div
          className={cn(
            'prose prose-sm prose-headings:my-3 prose-a:hover:text-blue-600 prose-a:duration-150 prose-a:text-blue-500 prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-hr:my-6 prose-p:leading-normal prose-p:text-gray-800 prose-headings:font-semibold max-w-none',
            type === KimMessageType.AiReasoning && 'animate-pulse italic',
          )}
        >
          {rendered}
        </div>

        {type === KimMessageType.Ai && isComplete && (
          <motion.div
            className="mt-6 flex w-full justify-between gap-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.15, delay: 0.2 }}
          >
            <div className="flex gap-1">
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" onClick={copyToClipboard} className="p-1 px-1.5">
                    <AnimatePresence mode="wait">
                      {copied ? (
                        <motion.span
                          key="check"
                          initial={{ opacity: 0, filter: 'blur(2px)' }}
                          animate={{ opacity: 1, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, filter: 'blur(2px)' }}
                          transition={{ duration: 0.1 }}
                        >
                          <Icon className="lucide--check" />
                        </motion.span>
                      ) : (
                        <motion.span
                          key="copy"
                          initial={{ opacity: 0, filter: 'blur(2px)' }}
                          animate={{ opacity: 1, filter: 'blur(0px)' }}
                          exit={{ opacity: 0, filter: 'blur(2px)' }}
                          transition={{ duration: 0.1 }}
                        >
                          <Icon className="lucide--copy" />
                        </motion.span>
                      )}
                    </AnimatePresence>
                  </Button>
                </TooltipTrigger>

                <TooltipContent>Copy answer to clipboard</TooltipContent>
              </Tooltip>

              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="ghost" className="p-1 px-1.5">
                    <Icon className="lucide--arrow-down-to-line" />
                  </Button>
                </TooltipTrigger>

                <TooltipContent>Download as Word document</TooltipContent>
              </Tooltip>
            </div>

            <div>
              {firstReference && onReferenceClick && (
                <Button
                  variant={'white'}
                  size={'sm'}
                  className="font-inter"
                  onClick={() => onReferenceClick(firstReference.id, firstReference.refType)}
                >
                  <Icon className="lucide--book-open" />
                  View references in answer
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
}
